<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { usePurchase } from '../composables/usePurchase'
import { useSubscription } from '../composables/useSubscription'
import { createShowPaymentMethodSelector } from '../composables/usePaymentMethodDisplay'
import { useBottomSwiper } from '../composables/useBottomSwiper'
import SubscribeItemContent from './SubscribeItemContent.vue'
import PurchaseItemContent from './PurchaseItemContent.vue'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqPaymentPopupAddToCart } from '@/composables/fbq/useFbqPaymentPopupAddToCart'
import { useUserStore } from '@/stores'
import { VIP_TYPE } from '@/constants'
import 'swiper/css'

const props = defineProps<{
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
  dark?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'update:membership-only', val: boolean): void
}>()

const config: ChapterConfig = getChapterConfig()

const userStore = useUserStore()

const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})

const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
  productLists: subscribeProductLists,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const active = ref(0)

const currentPaymentMethod = computed({
  get: () => {
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value
    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

const availablePaymentMethods = computed(() => {
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []

  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods
  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    if (p === s)
      return purchaseMethods
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  return purchaseMethods
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  const currentMethod = Number(currentPaymentMethod.value)
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

const hasOnlySubscribes = computed(() => {
  return (!templateProducts.value || templateProducts.value.length === 0) && subscribes.value?.length > 0
})

// 无充值档位 + 订阅档位全量为「畅读会员」(VIP_TYPE.ALL)
const isMembershipOnly = computed(() => {
  if (!loaded.value)
    return false
  const noRecharge = !templateProducts.value || templateProducts.value.length === 0
  const subs = subscribes.value || []
  return noRecharge && subs.length > 0 && subs.every((item: any) => Number(item?.subscribeType) === VIP_TYPE.ALL)
})

watch(isMembershipOnly, val => emit('update:membership-only', val), { immediate: true })

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function handleSubscribeProducts(item: any) {
  selectSubscriptPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

const subscribeSwiperRef = ref<HTMLElement>()
const {
  slideIndex,
  init: initSwiper,
  destroy: destroySwiper,
  onBulletClick,
} = useBottomSwiper(subscribeSwiperRef, () => subscribes.value)

function shouldInitSwiper() {
  return loaded.value && subscribes.value?.length > 0 && !hasOnlySubscribes.value
}

watch(
  () => loaded.value,
  (val) => {
    if (val && shouldInitSwiper())
      initSwiper()
  },
  { immediate: true },
)

watch(
  () => subscribes.value,
  (newVal) => {
    if (loaded.value && newVal?.length && !hasOnlySubscribes.value)
      initSwiper()
  },
)

watch(
  currentPaymentMethod,
  () => {
    if (shouldInitSwiper())
      initSwiper()
    else
      destroySwiper()
  },
)

function handleVisibilityChange() {
  if (!document.hidden && shouldInitSwiper())
    initSwiper()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  destroySwiper()
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

const { reportViewItem: reportPurchaseViewItem } = useAxonViewItem()
const { reportViewItem: reportSubscribeViewItem } = useAxonViewItem()

watch(templateProducts, (val) => {
  if (val.length)
    reportPurchaseViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

watch(subscribes, (val) => {
  if (val.length)
    reportSubscribeViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAll: reportFbqAddToCartAll } = useFbqPaymentPopupAddToCart()
watch(
  loaded,
  (isLoaded) => {
    if (isLoaded)
      reportFbqAddToCartAll(purchaseProductLists.value, subscribeProductLists.value)
  },
  { immediate: true },
)

const vipBg = config.colors.vipBg
const purpleTextGradient = config.colors.purpleTextGradient
const pinkGradient = config.colors.pinkGradient

function getSubscribeItemStyle(item: any) {
  return {
    background: getIsImageBackground(item)
      ? `url(${config.colors.memberSubscribeBg}) no-repeat`
      : config.colors.subscribeActiveBg,
    backgroundSize: '100% 100%',
  }
}
</script>

<template>
  <div class="inline-panel-recharge w-full relative">
    <!-- 充值项（网格布局） -->
    <div class="flex flex-wrap justify-between">
      <div
        v-for="(item, index) in templateProducts"
        :key="index"
        class="inline-pay-card purchase-item-wrapper relative b-rd-8px w-48% mt-4"
        :class="{ 'is-active': active === index }"
        @click="handlePayTemplate(item, index)"
      >
        <PurchaseItemContent :item="item" :is-active="active === index" />
      </div>
    </div>

    <!-- 订阅 - 列表样式（只有订阅商品时） -->
    <div v-if="!userStore.$state.subscribe && subscribes?.length && hasOnlySubscribes" class="subscribe-list-container mt-4">
      <div class="subscribe-list">
        <div
          v-for="(item, index) in subscribes"
          :key="index"
          class="inline-subscribe-card subscribe-list-item rounded-8px mb-3"
          @click="handleSubscribeProducts(item)"
        >
          <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" :dark="dark" />
        </div>
      </div>
    </div>

    <!-- 订阅 - 轮播样式（有充值商品时） -->
    <div v-else-if="!userStore.$state.subscribe && subscribes?.length" ref="subscribeSwiperRef" class="inline-panel-banner swiper mt-4">
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in subscribes"
          :key="index"
          class="inline-subscribe-card swiper-slide swiper-item rounded-8px"
          @click="handleSubscribeProducts(item)"
        >
          <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" :dark="dark" />
        </div>
      </div>
      <div v-if="subscribes.length > 1" class="inline-panel-swiper-pagination flex items-center justify-center">
        <span
          v-for="(_, i) in subscribes"
          :key="i"
          class="swiper-pagination-bullet"
          :class="{ 'swiper-pagination-bullet-active': i === slideIndex }"
          @click="onBulletClick(i)"
        />
      </div>
    </div>

    <!-- 支付方式选择器（底部，large 变体） -->
    <template v-if="showPaymentMethodSelector">
      <PaymentMethodSelector
        variant="large"
        :transparent="true"
        :dark="dark"
        :model-value="currentPaymentMethod"
        :available-methods="availablePaymentMethods"
        @update:model-value="val => currentPaymentMethod = val"
      />
    </template>

    <div v-if="!loaded" class="h-160px flex items-center justify-center flex-col">
      <BasicSpinner class="mx-auto" />
      <div class="mt-2 font-bold">
        {{ config.texts.loading }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pay-template-info:hover {
  border: 1px solid var(--primary-color);
  background-color: v-bind(vipBg);
}

.inline-pay-card,
.inline-subscribe-card {
  background: var(--pay-card-bg, #FFFFFF);
  border: 1px solid var(--pay-card-border, #ECEDEF);
  color: var(--pay-card-text, #32353A);

  // 覆盖卡片内 PurchaseItemContent/SubscribeItemContent 的品牌级文本色
  :deep(.text-n-text-1),
  :deep(.text-n-text-2) {
    color: var(--pay-card-text, #32353A);
  }

  :deep(.text-n-text-2) {
    opacity: 0.7;
  }
}

.inline-pay-card {
  // 选中态背景与未选中保持一致，仅通过边框区分
  &.is-active {
    border-color: var(--primary-color);
  }
}

.swiper-item {
  height: 100px;
}

.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.subscribe-list-container {
  position: relative;
}

.subscribe-list {
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}

.subscribe-list-item {
  min-height: 91px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}
</style>

<style lang="scss">
.inline-panel-recharge {
  .inline-panel-banner {
    position: relative;
  }

  .inline-panel-swiper-pagination {
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    z-index: 10;
    pointer-events: none;
  }

  .swiper-pagination-bullet {
    width: 5px;
    height: 5px;
    margin: 0 3px;
    background: var(--pay-pagination-bullet, rgba(146, 150, 160, 0.4));
    border-radius: 3px;
    opacity: 1;
    pointer-events: auto;
  }

  .swiper-pagination-bullet-active {
    width: 14px;
    height: 5px;
    background: var(--pay-pagination-active, var(--primary-color));
    border-radius: 3px;
    opacity: 1;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 56px;
    height: 18px;
    background: v-bind(pinkGradient);
    border-radius: 8px 0px 12px 0px;
    font-weight: 400;
    font-size: 10px;
    color: white !important;
    text-align: center;
  }
}
</style>
